import { type FC, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import CustomModalHeader from "~/features/scanDmc/components/CustomModalHeader/CustomModalHeader";
import { Button, CircularProgress, TextField } from "~/common/mui";
import { BackLink } from "~/common/components/Link";
import Modal from "~/common/components/Modal";
import { SimpleSelect } from "~/common/components/Select/SimpleSelect";
import { Icon } from "~/common/components/icon/Icon";
import { IconSize } from "~/common/components/icon/IconSize";
import { IconType } from "~/common/components/icon/IconType";
import { countTotalSize, FileUpload, MaxSizeError } from "~/features/scanDmc/components/FileUpload/FileUpload";
import ScanResultAlert from "~/features/scanDmc/components/ScanResultAlert/ScanResultAlert";
import classes from "~/features/scanDmc/components/ContactFormOverlay/ContactFormOverlay.module.less";
import { useTranslation } from "react-i18next";
import { type ScanResultType } from "~/features/scanDmc/types/ScanDMC";
import type { ContactFormData } from "~/features/scanDmc/api/apiSendDMCContactForm";
import { apiSendDMCContactForm } from "~/features/scanDmc/api/apiSendDMCContactForm";
import { userIsLoggedIn } from "~/domains/user/utils/roles";
import {
  EMPTY_TITLE_CODE,
  useSalutationOptions,
  useTitleOptions,
} from "~/features/scanDmc/components/ContactFormOverlay/dmcContactFormUtils";
import { trackDmcForm } from "~/app/analytics/ga/ga";
import { EMAIL_PATTERN } from "~/utils/constants";

const MAX_FILE_SIZE = 10_000_000; // 10MB

interface ContactFormOverlayProps {
  showAlert?: boolean;
  onClose: () => void;
  scanResult: ScanResultType;
  dmcCode: string;
}

const INITIAL_VALUES: ContactFormData = (() => {
  let base: ContactFormData = {
    titleCode: EMPTY_TITLE_CODE,
    salutationCode: "",
    firstName: "",
    lastName: "",
    email: "",
    description: "",
    dmcCode: "",
    files: [],
  };
  if (userIsLoggedIn()) {
    const { firstName, lastName, titleCode, salutationCode, email } = window.SCHA.user;
    base = {
      ...base,
      firstName,
      lastName,
      titleCode,
      salutationCode,
      email,
    };
  }
  return base;
})();

type Step = "form" | "sending" | "sendResult";

const ContactFormOverlay: FC<ContactFormOverlayProps> = ({ showAlert, onClose, scanResult, dmcCode }) => {
  const { t } = useTranslation();
  const { control, register, handleSubmit } = useForm({ defaultValues: { ...INITIAL_VALUES, dmcCode } });

  const [step, setStep] = useState<Step>("form");

  const onSubmit = async (data: ContactFormData) => {
    setStep("sending");
    const result = await apiSendDMCContactForm(data);
    setStep("sendResult");
    if (import.meta.env.DEV) {
      if (!result) {
        console.error("Error when sending contact form");
      }
    }
    trackDmcForm(scanResult, dmcCode, result);
  };

  const titleOptions = useTitleOptions();
  const salutationOptions = useSalutationOptions();

  const showSpinner = step === "sending";
  const form = (
    <div className={classes.formRoot}>
      <BackLink className={classes.backLink} onClick={onClose}>
        {t("page.scandmc.back")}
      </BackLink>
      <form className={classes.formContainer}>
        {showAlert && <ScanResultAlert scanResult={scanResult} />}
        <Controller
          name={"titleCode"}
          control={control}
          render={({ field: { ref, ...inputProps }, fieldState: { error } }) => (
            <SimpleSelect
              inputRef={ref}
              fullWidth
              className={classes.full}
              values={titleOptions}
              label={t("page.scandmc.form.title")}
              error={!!error}
              {...inputProps}
            />
          )}
        />

        <Controller
          name={"salutationCode"}
          control={control}
          rules={{ required: t("page.scandmc.form.error.required") }}
          render={({ field: { ref, ...inputProps }, fieldState: { error } }) => (
            <SimpleSelect
              inputRef={ref}
              fullWidth
              className={classes.full}
              values={salutationOptions}
              label={t("page.scandmc.form.salutation") + "*"}
              error={!!error}
              helperText={error?.message ?? null}
              {...inputProps}
            />
          )}
        />

        <Controller
          name={"firstName"}
          control={control}
          rules={{ required: t("page.scandmc.form.error.required") }}
          render={({ field: { ref, ...inputProps }, fieldState: { error } }) => (
            <TextField
              fullWidth
              label={t("page.scandmc.form.firstName") + "*"}
              error={!!error}
              inputRef={ref}
              {...inputProps}
              helperText={error?.message ?? null}
            />
          )}
        />
        <Controller
          name={"lastName"}
          control={control}
          rules={{ required: t("page.scandmc.form.error.required") }}
          render={({ field: { ref, ...inputProps }, fieldState: { error } }) => (
            <TextField
              fullWidth
              label={t("page.scandmc.form.lastName") + "*"}
              error={!!error}
              inputRef={ref}
              {...inputProps}
              helperText={error?.message ?? null}
            />
          )}
        />
        <Controller
          name={"email"}
          control={control}
          rules={{
            required: t("page.scandmc.form.error.required"),
            pattern: {
              value: EMAIL_PATTERN,
              message: t("page.contactUs.form.error.email.pattern"),
            },
          }}
          render={({ field: { ref, ...inputProps }, fieldState: { error } }) => (
            <TextField
              fullWidth
              error={!!error}
              label={t("page.scandmc.form.email") + "*"}
              inputRef={ref}
              {...inputProps}
              helperText={error?.message ?? null}
            />
          )}
        />
        <label className={classes.textAreaFormComponent}>
          <span>{t("page.scandmc.form.tellUsMore")}</span>
          <Controller
            name={"description"}
            control={control}
            render={({ field: { ref, ...inputProps } }) => (
              <TextField
                fullWidth
                className={classes.commentTextArea}
                inputRef={ref}
                {...inputProps}
                multiline
                minRows={4}
                helperText={null}
              />
            )}
          />
        </label>

        <Controller
          name={"files"}
          control={control}
          rules={{
            validate: (files) => countTotalSize(files) <= MAX_FILE_SIZE,
          }}
          render={({ field, fieldState: { error } }) => (
            <FileUpload
              {...field}
              multiple
              error={error && <MaxSizeError maxSize={MAX_FILE_SIZE} />}
              disabled={!!error}
            />
          )}
        />
      </form>
      {showSpinner && <CircularProgress className={classes.sendingIndicator} size={64} />}
    </div>
  );

  const sentScreen = (
    <div className={classes.sentScreen}>
      <div>
        <Icon size={IconSize.FONT_64} type={IconType.CheckCircle} />
      </div>
      <div className={classes.text}>
        <p>{t("page.scandmc.form.thankYou")}</p>
      </div>
    </div>
  );

  const footerButton =
    step === "form" ? (
      <Button onClick={() => handleSubmit(onSubmit)()} fullWidth>
        {t("page.scandmc.form.button.send")}
      </Button>
    ) : (
      <Button onClick={onClose} fullWidth>
        {t("page.scandmc.form.button.close")}
      </Button>
    );

  const showForm = step === "form" || step === "sending";
  const showResultScreen = step === "sendResult";

  return (
    <div>
      <Modal
        headerOverride={<CustomModalHeader />}
        children={
          <>
            {showForm && form}
            {showResultScreen && sentScreen}
          </>
        }
        footerChildren={footerButton}
        modalClassName="scha__modal--scanner"
        onClose={onClose}
      />
    </div>
  );
};

export default ContactFormOverlay;
